/**
 * useKeystoneConversationMeta — read the persona NAME and title of an existing
 * Keystone conversation (U8 decision #3; `title` widened for U3's dock header).
 *
 * U6 persists the persona name on the conversation; U4 passes it through the
 * proxy's create + get/list responses. After a reload there is no locally-picked
 * `PersonaListItem` (that state is per-session), so the chip is sourced from the
 * conversation's `personaName` returned by GET /api/keystone/conversations/:id.
 *
 * `title` rides the SAME response (`ConversationSchema.title`) — U3's
 * dock header reads it here rather than issuing a second GET (see
 * `plans/flow-global-chat-dock/coding-standards.md` "Do NOT Create").
 *
 * Both fields return `null` while loading or when absent (treat missing/null
 * as "nothing bound yet" — U4 note, applies identically to title).
 */

import { useEffect, useState } from 'react';
import { useApi } from '../../../lib/api';
import { logger } from '../../../lib/logger';
import { ApiProblemError, fetchJson } from '../../../lib/apiFetchJson';
import { ConversationSchema } from '@clay-run/keystone-contract';

export function useKeystoneConversationMeta(
  conversationId: string | null,
): { personaName: string | null; title: string | null } {
  const { apiFetch } = useApi();
  const [personaName, setPersonaName] = useState<string | null>(null);
  const [title, setTitle] = useState<string | null>(null);

  useEffect(() => {
    if (!conversationId) {
      setPersonaName(null);
      setTitle(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const data = await fetchJson(
          apiFetch,
          ConversationSchema,
          `/api/keystone/conversations/${conversationId}`,
        );
        if (!cancelled) {
          setPersonaName(data.personaName ?? null);
          setTitle(data.title ?? null);
        }
      } catch (err) {
        // A non-2xx just means "no meta yet" (as before); only log real failures.
        if (err instanceof ApiProblemError && err.kind === 'http') {
          if (!cancelled) {
            setPersonaName(null);
            setTitle(null);
          }
          return;
        }
        logger.error(
          { err: err as Error, conversationId },
          '[useKeystoneConversationMeta] fetch error',
        );
        if (!cancelled) {
          setPersonaName(null);
          setTitle(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId, apiFetch]);

  return { personaName, title };
}
